import { useCallback, useMemo, useState } from 'react';
import { uniqueId } from '@utils/uniqueId';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { TextInput, Textarea } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import type { CountryDto, ProductTypeDto } from '@/types/tenant';
import type { FormConfigSummary } from '@/types/form';
import FormEntryCard from './FormEntryCard';
import type { FormEntryWithId } from './FormEntryCard';
import type { TabWithClientIds } from './types';
import styles from './TabContentEditor.module.scss';

// ============================================
// TYPES
// ============================================

interface TabContentEditorProps {
  tab: TabWithClientIds;
  tabIndex: number;
  onTabChange: (tabIndex: number, updated: TabWithClientIds) => void;
  formConfigs: FormConfigSummary[];
  onFormSelectorFocus: () => void;
  countries: CountryDto[];
  productTypes: ProductTypeDto[];
  countriesLoading: boolean;
  productTypesLoading: boolean;
  tabNameError?: string;
  formErrors?: Map<string, { country?: string; productType?: string }>;
}

// ============================================
// COMPONENT
// ============================================

function TabContentEditor({
  tab,
  tabIndex,
  onTabChange,
  formConfigs,
  onFormSelectorFocus,
  countries,
  productTypes,
  countriesLoading,
  productTypesLoading,
  tabNameError,
  formErrors,
}: TabContentEditorProps) {
  const [showFormSelector, setShowFormSelector] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Filter out already-added form configs from the dropdown
  const addedFormConfigIds = useMemo(
    () => new Set(tab.forms.map((f) => f.formConfigId)),
    [tab.forms],
  );

  const availableFormOptions = useMemo(
    () =>
      formConfigs
        .filter((fc) => !addedFormConfigIds.has(fc.id))
        .map((fc) => ({ label: fc.formName, value: fc.id })),
    [formConfigs, addedFormConfigIds],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onTabChange(tabIndex, { ...tab, name: e.target.value });
    },
    [tab, tabIndex, onTabChange],
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onTabChange(tabIndex, { ...tab, description: e.target.value });
    },
    [tab, tabIndex, onTabChange],
  );

  const handleFormChange = useCallback(
    (formIndex: number, updated: FormEntryWithId) => {
      const updatedForms = [...tab.forms];
      updatedForms[formIndex] = updated;
      onTabChange(tabIndex, { ...tab, forms: updatedForms });
    },
    [tab, tabIndex, onTabChange],
  );

  const handleFormRemove = useCallback(
    (formIndex: number) => {
      const updatedForms = tab.forms.filter((_, i) => i !== formIndex);
      onTabChange(tabIndex, { ...tab, forms: updatedForms });
    },
    [tab, tabIndex, onTabChange],
  );

  const handleFormSelect = useCallback(
    (formConfigId: string) => {
      const formName = formConfigs.find((fc) => fc.id === formConfigId)?.formName;
      const newEntry: FormEntryWithId = {
        _clientId: uniqueId('form'),
        formConfigId,
        formName,
        accessLevel: 'READ_WRITE',
        filters: {
          country: { matchAll: true, values: [] },
          productType: { matchAll: true, values: [] },
        },
        attributes: {
          isDefault: false,
          isMandatory: false,
        },
      };
      onTabChange(tabIndex, { ...tab, forms: [...tab.forms, newEntry] });
      setShowFormSelector(false);
    },
    [tab, tabIndex, onTabChange, formConfigs],
  );

  const handleShowSelector = useCallback(() => {
    onFormSelectorFocus();
    setShowFormSelector(true);
  }, [onFormSelectorFocus]);

  const handleCancelSelector = useCallback(() => {
    setShowFormSelector(false);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = tab.forms.findIndex((f) => f._clientId === active.id);
      const newIndex = tab.forms.findIndex((f) => f._clientId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...tab.forms];
      const [moved] = reordered.splice(oldIndex, 1);
      if (!moved) return;
      reordered.splice(newIndex, 0, moved);

      onTabChange(tabIndex, { ...tab, forms: reordered });
    },
    [tab, tabIndex, onTabChange],
  );

  const sortableIds = tab.forms.map((f) => f._clientId);

  return (
    <div className={styles.root}>
      <div className={styles.fields}>
        <div className={styles.nameField}>
          <TextInput
            label="Tab Name"
            isRequired
            value={tab.name}
            onChange={handleNameChange}
            placeholder="Enter tab name"
            error={tabNameError}
            fullWidth
          />
        </div>
        <div className={styles.descriptionField}>
          <Textarea
            label="Tab Description"
            value={tab.description}
            onChange={handleDescriptionChange}
            placeholder="Enter tab description"
            rows={3}
            fullWidth
          />
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className={styles.formEntries}>
            {tab.forms.map((entry, i) => (
              <FormEntryCard
                key={entry._clientId}
                entry={entry}
                index={i}
                onChange={handleFormChange}
                onRemove={handleFormRemove}
                formConfigs={formConfigs}
                countries={countries}
                productTypes={productTypes}
                countriesLoading={countriesLoading}
                productTypesLoading={productTypesLoading}
                countryError={formErrors?.get(entry._clientId)?.country}
                productTypeError={formErrors?.get(entry._clientId)?.productType}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {showFormSelector ? (
        <div className={styles.addFormSelector} onFocusCapture={onFormSelectorFocus}>
          <Select
            options={availableFormOptions}
            value=""
            onChange={handleFormSelect}
            placeholder="Search and select a form..."
            searchable
            fullWidth
          />
          <button
            type="button"
            className={styles.cancelSelector}
            onClick={handleCancelSelector}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.addFormButton}
          onClick={handleShowSelector}
        >
          + Add Form
        </button>
      )}
    </div>
  );
}

export default TabContentEditor;
